import { Component } from '@theme/component';

/**
 * A custom element that crossfades hero slides.
 *
 * Slides are stacked and toggled with `aria-hidden`; the dots act as tabs.
 * Autoplay pauses on hover, on focus within, when the tab is hidden, and under
 * reduced-motion.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides - The slide elements.
 * @property {HTMLElement} [dotList] - The container the dots are built into.
 *
 * @extends Component<Refs>
 */
class StenandoHero extends Component {
  requiredRefs = ['slides'];

  #index = 0;
  #timer = null;

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  connectedCallback() {
    super.connectedCallback();

    this.#buildDots();
    this.#show(0);

    if (this.refs.slides.length > 1) {
      this.addEventListener('pointerenter', this.#pause);
      this.addEventListener('pointerleave', this.#play);
      this.addEventListener('focusin', this.#pause);
      this.addEventListener('focusout', this.#play);
      document.addEventListener('visibilitychange', this.#onVisibility);
      this.#play();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#pause();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  /**
   * Builds one dot per slide. The section renders an empty list because a
   * block cannot count its sibling blocks reliably.
   */
  #buildDots() {
    const { slides, dotList } = this.refs;
    if (!dotList || slides.length < 2) return;

    const template = dotList.dataset.dotLabel ?? 'Show slide %s';

    this.#dots = slides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'stenando-hero__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-selected', String(index === 0));
      dot.tabIndex = index === 0 ? 0 : -1;

      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = template.replace('%s', String(index + 1));
      dot.append(label);

      return dot;
    });

    dotList.append(...this.#dots);
  }

  /**
   * Selects the clicked dot.
   * @param {MouseEvent} event - The click event.
   */
  handleClick(event) {
    const dot = /** @type {HTMLElement} */ (event.target).closest('[role="tab"]');
    if (!dot) return;

    const index = this.#dots.indexOf(/** @type {HTMLButtonElement} */ (dot));
    if (index === -1) return;

    this.#show(index);
    this.#restart();
  }

  /**
   * Moves between slides with the arrow, Home and End keys.
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleKeydown(event) {
    if (!(/** @type {HTMLElement} */ (event.target).matches('[role="tab"]'))) return;

    const last = this.refs.slides.length - 1;
    let next = null;

    if (event.key === 'ArrowRight') next = this.#index === last ? 0 : this.#index + 1;
    if (event.key === 'ArrowLeft') next = this.#index === 0 ? last : this.#index - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;

    if (next === null) return;

    event.preventDefault();
    this.#show(next);
    this.#dots[next]?.focus();
    this.#restart();
  }

  /**
   * Reveals one slide and marks its dot selected.
   * @param {number} index - The slide index to show.
   */
  #show(index) {
    const { slides } = this.refs;

    slides.forEach((slide, position) => {
      slide.setAttribute('aria-hidden', String(position !== index));
    });

    this.#dots.forEach((dot, position) => {
      const selected = position === index;
      dot.setAttribute('aria-selected', String(selected));
      dot.tabIndex = selected ? 0 : -1;
    });

    this.#index = index;
  }

  #play = () => {
    if (this.#timer) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const seconds = Number(this.getAttribute('autoplay'));
    if (!seconds) return;

    this.#timer = setInterval(() => {
      this.#show((this.#index + 1) % this.refs.slides.length);
    }, seconds * 1000);
  };

  #pause = () => {
    if (!this.#timer) return;
    clearInterval(this.#timer);
    this.#timer = null;
  };

  #restart() {
    this.#pause();
    this.#play();
  }

  #onVisibility = () => {
    if (document.hidden) this.#pause();
    else this.#play();
  };
}

if (!customElements.get('stenando-hero-component')) {
  customElements.define('stenando-hero-component', StenandoHero);
}
